import { Formik, FormikProps } from 'formik';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import useCollapse from 'react-collapsed';
import { UseCollapseOutput } from 'react-collapsed/dist/types';
import { TransProps, useTranslation } from 'react-i18next';

import { BillingAddressFormDecision, FormFieldType } from '../../../enums';
import { BillingAddressFormData } from '../../../interfaces';
import { UseState } from '../../../types';

import { useBillingAddressFormValidation } from './billingAddressForm.hooks';
import { ButtonsContainer, Container, FieldsRow, FormikForm, StyledFormField, StyledRadioButton } from './billingAddressForm.styled';
import { BillingAddressFormProps, UseBillingAddressForm } from './billingAddressForm.types';

export const BillingAddressForm: React.FC<BillingAddressFormProps> = forwardRef((
  props: BillingAddressFormProps, ref: React.Ref<FormikProps<BillingAddressFormData>>
): JSX.Element => {
  const { initialValues, isFormSubmitted, className }: BillingAddressFormProps = props;
  const { baseSchema, schemaForCompany }: UseBillingAddressForm = useBillingAddressFormValidation();
  const { t }: TransProps<never> = useTranslation();
  const [activeRadioValue, setActiveRadioValue]: UseState<BillingAddressFormDecision | null>
    = useState<BillingAddressFormDecision | null>(null);
  const { getCollapseProps, setExpanded }: UseCollapseOutput = useCollapse();
  const localRef: React.MutableRefObject<FormikProps<BillingAddressFormData> | null> = useRef(null);

  useEffect(
    (): void => {
      setExpanded(activeRadioValue === BillingAddressFormDecision.Company);
      if (activeRadioValue === BillingAddressFormDecision.Individual) {
        if (localRef.current) {
          let newValues: BillingAddressFormData = {} as BillingAddressFormData;
          Object.entries(localRef.current.values)
            .filter(([key]: [string, string]): boolean => !['taxId', 'companyName'].includes(key))
            .map((
              [key, value]: [string, string | undefined | null]
            ): Record<string, string | undefined | null> => newValues = { ...newValues, [key]: value });
          localRef.current.setValues(newValues, false);
        }
      }
    },
    [activeRadioValue]
  );

  useEffect(
    (): void => {
      if (initialValues.companyName) {
        setActiveRadioValue(BillingAddressFormDecision.Company);
      } else {
        setActiveRadioValue(BillingAddressFormDecision.Individual);
      }
    },
    [initialValues]
  );

  return (
    <Container>
      <ButtonsContainer>
        <StyledRadioButton
          data-testid='company'
          isActive={activeRadioValue === BillingAddressFormDecision.Company}
          label={t('onarte.ui.billingAddressForm.company.radioButton.label')}
          optionName={BillingAddressFormDecision.Company}
          onChange={(): void => setActiveRadioValue(BillingAddressFormDecision.Company)}
        />
        <StyledRadioButton
          data-testid='individual'
          isActive={activeRadioValue === BillingAddressFormDecision.Individual}
          label={t('onarte.ui.billingAddressForm.individual.radioButton.label')}
          optionName={BillingAddressFormDecision.Individual}
          onChange={(): void => setActiveRadioValue(BillingAddressFormDecision.Individual)}
        />
      </ButtonsContainer>
      <Formik
        className={className}
        initialValues={{ ...initialValues, country: 'Polska' }}
        onSubmit={(): void => undefined}
        enableReinitialize
        innerRef={(
          node: FormikProps<BillingAddressFormData> | null
        ): React.MutableRefObject<FormikProps<BillingAddressFormData> | null> => {
          localRef.current = node;
          if (ref) {
            (ref as React.MutableRefObject<FormikProps<BillingAddressFormData> | null>).current = node;
          }
          return localRef;
        }}
        validationSchema={activeRadioValue === BillingAddressFormDecision.Company ? schemaForCompany : baseSchema}
        validateOnChange={isFormSubmitted}
        validateOnBlur={isFormSubmitted}
      >
        {({ handleSubmit, setFieldValue, errors, values }: FormikProps<BillingAddressFormData>): JSX.Element => (
          <FormikForm onSubmit={handleSubmit}>
            <FieldsRow>
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.firstName.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.firstName}
                fieldName='firstName'
                validationMessage={isFormSubmitted ? errors.firstName : undefined}
              />
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.lastName.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.lastName}
                fieldName='lastName'
                validationMessage={isFormSubmitted ? errors.lastName : undefined}
              />
            </FieldsRow>
            <FieldsRow {...getCollapseProps()}>
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.companyName.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.companyName}
                fieldName='companyName'
                validationMessage={isFormSubmitted ? errors.companyName : undefined}
                withfullWidthFieldContainer
              />
            </FieldsRow>
            <FieldsRow>
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.street.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.street}
                fieldName='street'
                validationMessage={isFormSubmitted ? errors.street : undefined}
                withfullWidthFieldContainer
              />
            </FieldsRow>
            <FieldsRow>
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.houseNumber.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.houseNumber}
                fieldName='houseNumber'
                validationMessage={isFormSubmitted ? errors.houseNumber : undefined}
              />
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.apartmentNumber.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.apartmentNumber ?? ''}
                fieldName='apartmentNumber'
                validationMessage={isFormSubmitted ? errors.apartmentNumber : undefined}
              />
            </FieldsRow>
            <FieldsRow>
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.postalCode.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.postalCode}
                fieldName='postalCode'
                validationMessage={isFormSubmitted ? errors.postalCode : undefined}
                additionalFieldProps={{
                  format: '##-###'
                }}
              />
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.city.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.city}
                fieldName='city'
                validationMessage={isFormSubmitted ? errors.city : undefined}
              />
            </FieldsRow>
            <FieldsRow {...getCollapseProps()}>
              <StyledFormField
                label={t('onarte.ui.billingAddressForm.taxId.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={activeRadioValue === BillingAddressFormDecision.Company ? values.taxId : ''}
                fieldName='taxId'
                validationMessage={isFormSubmitted ? errors.taxId : undefined}
                withfullWidthFieldContainer
              />
            </FieldsRow>
            <FieldsRow>
              <StyledFormField
                disabled
                label={t('onarte.ui.billingAddressForm.country.label')}
                fieldType={FormFieldType.Input}
                setFieldValue={setFieldValue}
                value={values.country}
                fieldName='country'
                withfullWidthFieldContainer
              />
            </FieldsRow>
          </FormikForm>
        )}
      </Formik>
    </Container>
  );
});
